// Claim detail page — /warranty/:id (authenticated)
import { useState } from "react";
import { useParams, useNavigate } from "@tanstack/react-router";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { apiGet, apiPatch } from "@/lib/api/client";
import { SerialTimeline } from "./components/serial-timeline";
import { Wrench } from "lucide-react";

interface Claim {
  id: string; code: string; serialNo: string; productName: string; variantSku: string;
  customerId?: string; customerName?: string; receivedAt: string; issueDesc: string;
  resolutionType: string; resolutionNote?: string; repairTicketId?: string;
  status: string; resolvedAt?: string; createdAt: string;
}

const STATUS_OPTS = [
  { value: "received",    label: "Tiếp nhận" },
  { value: "in_progress", label: "Đang xử lý" },
  { value: "resolved",    label: "Hoàn thành" },
  { value: "cancelled",   label: "Huỷ" },
];

const RES_OPTS = [
  { value: "warranty",    label: "Bảo hành (miễn phí)" },
  { value: "paid_repair", label: "Sửa có phí" },
  { value: "rejected",    label: "Từ chối" },
];

const STATUS_COLOR: Record<string, string> = {
  received: "bg-blue-100 text-blue-700", in_progress: "bg-yellow-100 text-yellow-700",
  resolved: "bg-green-100 text-green-700", cancelled: "bg-gray-100 text-gray-500",
};

function fmt(d?: string) {
  if (!d) return "—";
  return new Date(d).toLocaleString("vi-VN", { day: "2-digit", month: "2-digit", year: "numeric", hour: "2-digit", minute: "2-digit" });
}

export function ClaimDetailPage() {
  const { id } = useParams({ from: "/warranty/$id" as any });
  const navigate = useNavigate();
  const qc = useQueryClient();
  const [editing, setEditing] = useState(false);
  const [form, setForm] = useState({ status: "", resolutionType: "", resolutionNote: "" });

  const { data, isLoading } = useQuery({
    queryKey: ["warranty-claim", id],
    queryFn: () => apiGet<Claim>(`/v1/warranty/claims/${id}`),
    select: (d) => d as unknown as Claim,
  });

  const cl = data as unknown as Claim | undefined;

  const patch = useMutation({
    mutationFn: (body: Record<string, string>) =>
      apiPatch(`/v1/warranty/claims/${id}`, body),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["warranty-claim", id] });
      qc.invalidateQueries({ queryKey: ["warranty-claims"] });
      setEditing(false);
    },
  });

  function openEdit() {
    if (!cl) return;
    setForm({ status: cl.status, resolutionType: cl.resolutionType, resolutionNote: cl.resolutionNote ?? "" });
    setEditing(true);
  }

  function submitEdit() {
    const body: Record<string, string> = {};
    if (form.status)         body.status = form.status;
    if (form.resolutionType) body.resolutionType = form.resolutionType;
    if (form.resolutionNote !== undefined) body.resolutionNote = form.resolutionNote;
    patch.mutate(body);
  }

  if (isLoading) return <div className="py-12 text-center text-muted-foreground">Đang tải...</div>;
  if (!cl)       return <div className="py-12 text-center text-destructive">Không tìm thấy phiếu BH</div>;

  return (
    <div className="mx-auto max-w-3xl space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold">{cl.code}</h1>
          <p className="text-sm text-muted-foreground">Phiếu bảo hành · {fmt(cl.receivedAt)}</p>
        </div>
        <div className="flex gap-2">
          <span className={`rounded-full px-3 py-1 text-xs font-medium ${STATUS_COLOR[cl.status] ?? ""}`}>
            {STATUS_OPTS.find((o) => o.value === cl.status)?.label ?? cl.status}
          </span>
          <button onClick={openEdit}
            className="rounded-md border px-3 py-1 text-sm hover:bg-accent">Cập nhật</button>
        </div>
      </div>

      <div className="grid gap-6 md:grid-cols-2">
        {/* Claim info */}
        <div className="space-y-4 rounded-xl border bg-card p-5">
          <h2 className="font-semibold">Thông tin phiếu</h2>
          <Row label="Serial"       value={cl.serialNo} mono />
          <Row label="Sản phẩm"     value={`${cl.productName} (${cl.variantSku})`} />
          <Row label="Khách hàng"   value={cl.customerName ?? "—"} />
          <Row label="Mô tả lỗi"    value={cl.issueDesc} />
          <Row label="Loại xử lý"   value={RES_OPTS.find((o) => o.value === cl.resolutionType)?.label ?? cl.resolutionType} />
          {cl.resolutionNote && <Row label="Ghi chú xử lý" value={cl.resolutionNote} />}
          {cl.resolvedAt && <Row label="Hoàn thành lúc" value={fmt(cl.resolvedAt)} />}

          {/* Repair ticket link / create */}
          {cl.resolutionType === "paid_repair" && !cl.repairTicketId && (
            <div className="rounded-lg border border-dashed p-3 text-sm text-muted-foreground">
              <div className="flex items-center gap-2">
                <Wrench size={14} />
                <span>Chưa có phiếu sửa chữa</span>
              </div>
              <button
                onClick={() => navigate({ to: `/repair/new?claim=${id}` as string })}
                className="mt-2 rounded border px-3 py-1 text-xs font-medium hover:bg-accent transition-colors"
              >
                Tạo phiếu sửa
              </button>
            </div>
          )}
          {cl.repairTicketId && (
            <div className="rounded-lg border bg-muted/30 p-3 text-sm">
              <div className="flex items-center gap-2 text-muted-foreground">
                <Wrench size={14} />
                <span>Đã có phiếu sửa chữa</span>
              </div>
              <button
                onClick={() => navigate({ to: `/repair/${cl.repairTicketId}` as string })}
                className="mt-1 text-primary underline text-xs"
              >
                Xem phiếu sửa →
              </button>
            </div>
          )}
        </div>

        {/* Timeline */}
        <div className="space-y-3 rounded-xl border bg-card p-5">
          <h2 className="font-semibold">Lịch sử serial</h2>
          <SerialTimeline serialNo={cl.serialNo} />
        </div>
      </div>

      {/* Edit dialog */}
      {editing && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40">
          <div className="w-full max-w-md rounded-xl bg-card p-6 shadow-xl space-y-4">
            <h3 className="font-semibold text-lg">Cập nhật phiếu BH</h3>

            <label className="block space-y-1 text-sm">
              <span className="text-muted-foreground">Trạng thái</span>
              <select
                className="w-full rounded-md border bg-background px-3 py-2"
                value={form.status}
                onChange={(e) => setForm((f) => ({ ...f, status: e.target.value }))}
              >
                {STATUS_OPTS.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
              </select>
            </label>

            <label className="block space-y-1 text-sm">
              <span className="text-muted-foreground">Loại xử lý</span>
              <select
                className="w-full rounded-md border bg-background px-3 py-2"
                value={form.resolutionType}
                onChange={(e) => setForm((f) => ({ ...f, resolutionType: e.target.value }))}
              >
                {RES_OPTS.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
              </select>
            </label>

            <label className="block space-y-1 text-sm">
              <span className="text-muted-foreground">Ghi chú xử lý</span>
              <textarea
                rows={3}
                className="w-full rounded-md border bg-background px-3 py-2"
                value={form.resolutionNote}
                onChange={(e) => setForm((f) => ({ ...f, resolutionNote: e.target.value }))}
              />
            </label>

            {patch.isError && (
              <p className="text-sm text-destructive">Có lỗi xảy ra, vui lòng thử lại.</p>
            )}

            <div className="flex justify-end gap-3">
              <button onClick={() => setEditing(false)}
                className="rounded-md border px-4 py-2 text-sm">Huỷ</button>
              <button onClick={submitEdit} disabled={patch.isPending}
                className="rounded-md bg-primary px-4 py-2 text-sm font-medium text-primary-foreground disabled:opacity-50">
                {patch.isPending ? "Đang lưu..." : "Lưu"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function Row({ label, value, mono }: { label: string; value: string; mono?: boolean }) {
  return (
    <div className="flex justify-between gap-3 text-sm">
      <span className="text-muted-foreground shrink-0">{label}</span>
      <span className={`text-right ${mono ? "font-mono" : "font-medium"}`}>{value}</span>
    </div>
  );
}
